import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, ChevronDown } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";

const STORAGE_KEY = "vexorqa.consent.v2";

const defaultConsent = {
  necessary: true,
  analytics: false,
  marketing: false,
  timestamp: null,
};

const gtag = (...args) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(args);
};

export const applyConsentToGtag = (consent) => {
  const toState = (v) => (v ? "granted" : "denied");
  gtag("consent", "update", {
    ad_storage: toState(consent.marketing),
    ad_user_data: toState(consent.marketing),
    ad_personalization: toState(consent.marketing),
    analytics_storage: toState(consent.analytics),
    functionality_storage: "granted",
    security_storage: "granted",
    personalization_storage: toState(consent.marketing),
  });
};

export const readStoredConsent = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return { ...defaultConsent, ...parsed };
  } catch (e) {
    return null;
  }
};

export const CookieBanner = ({ forceOpen, onClose }) => {
  const { t, lang } = useI18n();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState(defaultConsent);

  useEffect(() => {
    const stored = readStoredConsent();
    if (!stored) {
      setVisible(true);
    } else {
      applyConsentToGtag(stored);
      setPrefs(stored);
    }
  }, []);

  useEffect(() => {
    if (forceOpen) setVisible(true);
  }, [forceOpen]);

  const persist = (next) => {
    const withTs = { ...next, timestamp: new Date().toISOString() };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(withTs));
    } catch (e) {}
    applyConsentToGtag(withTs);
    setPrefs(withTs);
    setVisible(false);
    if (onClose) onClose();
  };

  const acceptAll = () =>
    persist({ necessary: true, analytics: true, marketing: true });
  const rejectAll = () =>
    persist({ necessary: true, analytics: false, marketing: false });
  const savePrefs = () => persist(prefs);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[60] px-3 sm:px-6 pb-3 sm:pb-6"
    >
      <div className="mx-auto max-w-5xl bg-white text-slate-900 border border-slate-200 shadow-2xl rounded-sm overflow-hidden">
        <div className="p-5 sm:p-7 flex items-start gap-4">
          <span className="hidden sm:flex h-10 w-10 items-center justify-center bg-emerald-100 border border-emerald-300 rounded-sm text-emerald-700">
            <Shield className="h-5 w-5" />
          </span>
          <div className="flex-1">
            <h2
              id="cookie-banner-title"
              className="text-lg font-semibold text-slate-900 tracking-tight"
            >
              {t("cookies.banner.title")}
            </h2>
            <p className="mt-2 text-sm text-slate-600 leading-relaxed">
              {t("cookies.banner.description")}{" "}
              <Link
                to={`/${lang}/cookie-policy`}
                className="underline text-emerald-700 hover:text-emerald-800"
                data-testid="cookie-banner-learn-more"
              >
                {t("cookies.banner.learn_more")}
              </Link>
            </p>

            <button
              type="button"
              data-testid="cookie-banner-customize-toggle"
              onClick={() => setShowDetails((v) => !v)}
              className="mt-3 inline-flex items-center gap-1 text-xs uppercase font-semibold tracking-widest text-slate-700 hover:text-emerald-700"
            >
              {t("cookies.banner.customize")}
              <ChevronDown
                className={`h-3.5 w-3.5 ${showDetails ? "rotate-180" : ""}`}
                aria-hidden="true"
              />
            </button>

            {showDetails && (
              <div className="mt-4 border-t border-slate-200 pt-4 space-y-3">
                {[
                  { key: "necessary", disabled: true },
                  { key: "analytics", disabled: false },
                  { key: "marketing", disabled: false },
                ].map((cat) => (
                  <label
                    key={cat.key}
                    className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-200 rounded-sm cursor-pointer"
                    data-testid={`cookie-cat-${cat.key}`}
                  >
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 accent-emerald-600"
                      disabled={cat.disabled}
                      checked={!!prefs[cat.key]}
                      onChange={(e) =>
                        setPrefs((p) => ({ ...p, [cat.key]: e.target.checked }))
                      }
                      data-testid={`cookie-cat-${cat.key}-checkbox`}
                    />
                    <span className="flex-1">
                      <span className="block text-sm font-semibold text-slate-900">
                        {t(`cookies.categories.${cat.key}.name`)}
                      </span>
                      <span className="block text-xs text-slate-600 mt-0.5">
                        {t(`cookies.categories.${cat.key}.description`)}
                      </span>
                    </span>
                  </label>
                ))}
              </div>
            )}

            <div className="mt-5 flex flex-col sm:flex-row gap-2 sm:gap-3">
              <button
                type="button"
                data-testid="cookie-btn-reject"
                onClick={rejectAll}
                className="px-4 py-2.5 text-sm font-medium border border-slate-300 text-slate-800 hover:border-slate-500 rounded-sm"
              >
                {t("cookies.banner.reject_all")}
              </button>
              {showDetails && (
                <button
                  type="button"
                  data-testid="cookie-btn-save"
                  onClick={savePrefs}
                  className="px-4 py-2.5 text-sm font-medium border border-emerald-700 text-emerald-800 hover:bg-emerald-50 rounded-sm"
                >
                  {t("cookies.banner.save")}
                </button>
              )}
              <button
                type="button"
                data-testid="cookie-btn-accept"
                onClick={acceptAll}
                className="px-4 py-2.5 text-sm font-semibold bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm sm:ml-auto"
              >
                {t("cookies.banner.accept_all")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieBanner;
